import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ArticleShell, { Ext } from "@/components/content/ArticleShell";

export const SAFETY_TEST = "pdx-safety-h1-2026-11";
type V = "A" | "B";
const KEY = "px_ab_safety";
const H1: Record<V, string> = {
  A: "GLP-1s sold online: how to tell what's real",
  B: "Is that online GLP-1 real? What the FDA says",
};
const COMPARE = "/compare/retatrutide-vs-zepbound-mounjaro-wegovy";
type Gtag = (...a: unknown[]) => void;
const gtag = (): Gtag | undefined => (window as unknown as { gtag?: Gtag }).gtag;

function useSafetyVariant(): V {
  const [v, setV] = useState<V>("A");
  useEffect(() => {
    let x: V;
    try {
      const s = window.localStorage.getItem(KEY);
      x = s === "A" || s === "B" ? s : Math.random() < 0.5 ? "A" : "B";
      window.localStorage.setItem(KEY, x);
    } catch { x = Math.random() < 0.5 ? "A" : "B"; }
    setV(x);
    const g = gtag();
    if (typeof g === "function") g("event", "ab_impression", { test_name: SAFETY_TEST, variant: x, source: "glp-1-online-safety" });
  }, []);
  return v;
}

// Visible FAQ text and FAQPage JSON-LD both render from this array, so they match word for word.
const faqs: { question: string; answer: string; sources: { label: string; href: string }[] }[] = [
  {
    question: "Are GLP-1 medicines sold online always the real thing?",
    answer: "No. The FDA has warned about unapproved and counterfeit GLP-1 products sold online.",
    sources: [{ label: "FDA: concerns with unapproved GLP-1 drugs", href: "https://www.fda.gov/drugs/postmarket-drug-safety-information-patients-and-providers/fdas-concerns-unapproved-glp-1-drugs-used-weight-loss" }],
  },
  {
    question: "Which GLP-1 medicines are FDA-approved for weight?",
    answer: "Wegovy shots (semaglutide, approved June 4, 2021), Zepbound (tirzepatide, approved Nov 8, 2023), the Wegovy pill (oral semaglutide, approved Dec 22, 2025) and Foundayo (orforglipron, approved April 1, 2026). Each is approved for adults along with diet and exercise.",
    sources: [
      { label: "Wegovy label", href: "https://www.accessdata.fda.gov/drugsatfda_docs/label/2026/215256s025lbl.pdf" },
      { label: "FDA: Zepbound approval", href: "https://www.fda.gov/news-events/press-announcements/fda-approves-new-medication-chronic-weight-management" },
      { label: "Novo Nordisk: Wegovy pill approval", href: "https://www.novonordisk-us.com/media/press-releases/wegovy-pill-fda-approval-obesity.html" },
      { label: "FDA: Foundayo approval", href: "https://www.fda.gov/news-events/press-announcements/fda-approves-first-new-molecular-entity-under-national-priority-voucher-program" },
    ],
  },
  {
    question: "Can pharmacies still make copies of semaglutide or tirzepatide?",
    answer: "The FDA declared the tirzepatide shortage over on Dec 19, 2024 and the semaglutide shortage over on Feb 21, 2025. That ended the shortage-based reason for pharmacies to make copies. Compounded drugs are not FDA-approved.",
    sources: [{ label: "FDA: compounding policies as GLP-1 supply stabilizes", href: "https://www.fda.gov/drugs/drug-safety-and-availability/fda-clarifies-policies-compounders-national-glp-1-supply-begins-stabilize" }],
  },
  {
    question: "Are \"research peptides\" like BPC-157 approved for people?",
    answer: "No. BPC-157, TB-500 and similar research peptides are not FDA-approved for any use in people.",
    sources: [{ label: "HHS notice, 91 FR 68526", href: "https://www.federalregister.gov/documents/2026/10/30/2026-22192/enforcement-discretion-policy-regarding-503a-compounding-using-certain-peptide-bulk-drug-substances" }],
  },
  {
    question: "Do approved GLP-1s carry safety warnings?",
    answer: "Yes. For example, Foundayo has an FDA boxed warning about a possible risk of thyroid C-cell tumors. People with a personal or family history of medullary thyroid cancer or MEN 2 should not take it. Wegovy and Zepbound carry the same boxed warning.",
    sources: [
      { label: "Foundayo label", href: "https://www.accessdata.fda.gov/drugsatfda_docs/label/2026/220934Orig1s000lbl.pdf" },
      { label: "Wegovy label", href: "https://www.accessdata.fda.gov/drugsatfda_docs/label/2026/215256s025lbl.pdf" },
      { label: "Zepbound label (DailyMed)", href: "https://dailymed.nlm.nih.gov/dailymed/drugInfo.cfm?setid=487cd7e7-434c-4925-99fa-aa80b1cc776b" },
    ],
  },
  {
    question: "What should I do before starting a GLP-1?",
    answer: "Talk to a licensed clinician. PeptideX doesn't give dosing advice.",
    sources: [],
  },
];

export default function Glp1OnlineSafety() {
  const variant = useSafetyVariant();
  const onCompare = () => {
    const g = gtag();
    if (typeof g === "function") g("event", "compare_click", { test_name: SAFETY_TEST, variant, source: "glp-1-online-safety", target: COMPARE });
  };
  return (
    <ArticleShell
      kicker="Safety"
      title={H1.A}
      heading={H1[variant]}
      description="What the FDA says about unapproved and fake GLP-1s sold online, which GLP-1s are FDA-approved for weight, and why pharmacy copies changed. Education only."
      path="/glp-1-online-safety"
      lastChecked="Nov 3, 2026"
      lastCheckedIso="2026-11-03"
      faqs={faqs.map(({ question, answer }) => ({ question, answer }))}
    >
      <p className="text-base">What the FDA says about unapproved and fake GLP-1s, in plain words.</p>
      {faqs.map(f => (
        <section key={f.question}>
          <h2 className="text-xl font-bold text-foreground mb-2">{f.question}</h2>
          <p>{f.answer}</p>
          {f.sources.length > 0 && (
            <p className="text-xs text-muted-foreground mt-2">
              Sources: {f.sources.map((s, i) => (<span key={s.href}>{i > 0 && " · "}<Ext href={s.href}>{s.label}</Ext></span>))}
            </p>
          )}
        </section>
      ))}
      <p>
        See how these medicines differ in FDA status and trial results:{" "}
        <Link to={COMPARE} onClick={onCompare} className="text-primary hover:underline">GLP-1 comparison</Link>.
      </p>
      <p className="text-xs text-muted-foreground">Educational info, not medical advice. Our answers link to primary sources.</p>
    </ArticleShell>
  );
}
